import { API_URL } from '../config';
import jwtDecode from 'jwt-decode'; // Import jwt-decode library
import api from './api';
import {
  getToken,
  setToken,
  setUser,
  getUser,
  logout,
  isAuthenticated,
} from './authSession';

export {
  getToken,
  setToken,
  setUser,
  getUser,
  logout,
  isAuthenticated,
};

// Normalize user data to ensure consistent format
const normalizeUserData = (userData) => {
  if (!userData) return null;
  
  return {
    ...userData,
    role: typeof userData.role === 'string' 
      ? { name: userData.role } 
      : userData.role,
  };
};

// Auth functions
export const login = async (email, password) => {
  try {
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();

    if (!response.ok) {
      const detail = data.detail || 'Login failed';
      const error = new Error(detail);
      error.response = { data: { detail } };
      throw error;
    }

    const normalizedUser = normalizeUserData(data.user);
    // Store both token and user data
    setToken(data.access_token);
    setUser(normalizedUser);
    
    return {
      token: data.access_token,
      user: normalizedUser
    };
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

export const exchangeEdmingleToken = async (token) => {
  const response = await fetch(`${API_URL}/auth/edmingle/exchange`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token }),
  });
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.detail || 'Edmingle SSO login failed');
  }

  return {
    token: data.access_token,
    user: normalizeUserData(data.user),
  };
};

export const getCurrentUser = async () => {
  try {
    const token = getToken();
    if (!token) {
      throw new Error('No token found');
    }

    const response = await fetch(`${API_URL}/auth/me`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const error = new Error(
        response.status === 401
          ? 'Session expired'
          : 'Unable to validate session'
      );
      error.status = response.status;
      throw error;
    }

    const userData = await response.json();
    const normalizedUser = normalizeUserData(userData);
    setUser(normalizedUser);
    return normalizedUser;
  } catch (error) {
    console.error('Get current user error:', error);
    throw error;
  }
};

export const getAuthHeader = () => {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const handleSSOToken = () => {
  const params = new URLSearchParams(window.location.search);
  const ssoData = params.get('sso_data');
  const redirect = params.get('redirect') || '/dashboard';

  if (ssoData) {
    try {
      // Decode the JWT containing auth data
      const authData = jwtDecode(ssoData);

      if (!authData || !authData.access_token || !authData.user) {
        throw new Error('Invalid SSO data');
      }

      // Store the token
      localStorage.setItem('token', authData.access_token);
      
      // Store user data
      localStorage.setItem('user', JSON.stringify(authData.user));
      
      // Clean up URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
      
      // Check for ticket_id in the auth data for email auto-login
      if (authData.ticket_id) {
        return `/tickets/${authData.ticket_id}`;
      }
      
      return redirect;
    } catch (error) {
      console.error('Error processing SSO data:', error);
      return '/login';
    }
  }
  
  return null;
};

export const handleEmailAutoLoginToken = () => {
  const params = new URLSearchParams(window.location.search);
  const emailAutoLoginToken = params.get('email_auto_login_token');
  
  if (emailAutoLoginToken) {
    try {
      // Decode the JWT containing auth data
      const authData = jwtDecode(emailAutoLoginToken);

      if (!authData || !authData.access_token || !authData.user) {
        throw new Error('Invalid email auto-login token');
      }

      // Store the token
      localStorage.setItem('token', authData.access_token);
      
      // Store user data
      localStorage.setItem('user', JSON.stringify(authData.user));
      
      // Clean up URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
      
      return `/tickets/${authData.ticket_id}`;
    } catch (error) {
      console.error('Error processing email auto-login token:', error);
      return '/login';
    }
  }
  
  return null;
};

export const authService = {
  login,
  logout,
  getCurrentUser,
  requestPasswordReset,
  verifyResetToken,
  completePasswordReset
};

async function requestPasswordReset(email) {
  const response = await api.post(`${API_URL}/auth/password-reset/request`, { 
    email_address: email 
  });
  return response.data;
}

async function verifyResetToken(token) {
  const response = await api.post(`${API_URL}/auth/password-reset/verify`, { token: token });
  return response.data;
}

async function completePasswordReset(token, newPassword) {
  const response = await api.post(`${API_URL}/auth/password-reset/complete`, {
    token,
    new_password: newPassword
  });
  return response.data;
}